import { layoutRect, layoutCoordinate } from "../utils/displayDensity";
import Papa from "papaparse";
import CsvExportButton from "../components/CsvExportButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useGlobalData } from "../contexts/DataContext";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import TextInputField from "../components/Fields/TextInputField";
import SteamVideoPreview from "../components/SteamVideoPreview";

// S3 CSV.GZ sources
const LATEST_FOLLOWERS_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_followers.csv.gz";
const FOLLOWERS_GROWTH_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/followers_growth.csv.gz";

/** Fetch a .csv.gz and return CSV text (native DecompressionStream if available). */
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  if ("DecompressionStream" in window && res.body) {
    try {
      const ds = new DecompressionStream("gzip");
      const stream = res.body.pipeThrough(ds);
      const reader = stream.getReader();
      const chunks = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      const blob = new Blob(chunks, { type: "text/plain;charset=utf-8" });
      return await blob.text();
    } catch (e) {
      try {
        return await res.text();
      } catch {
        throw e;
      }
    }
  }
  return await res.text();
}

/** Parse CSV text to array of objects (Papa header mode). */
function parseCsvText(csvText) {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: ({ data }) => resolve(data),
      error: (err) => reject(err),
    });
  });
}

/** fetch + gunzip (if needed) + parse */
async function loadCsvGz(url) {
  const text = await fetchGzipCsvText(url);
  return await parseCsvText(text);
}

function Followers() {
  const [games, setGames] = useState([]);
  const [displayedGames, setDisplayedGames] = useState([]);
  const [hiddenGames, setHiddenGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnreleasedOnly, setShowUnreleasedOnly] = useState(false);
  const [includeLowFollowerGames, setIncludeLowFollowerGames] = useState(false);
  const [sortType, setSortType] = useState("followers"); // followers | growth_7d | growth_30d
  const [hoveredId, setHoveredId] = useState(null);

  const { metadata } = useGlobalData();

  // 3D tilt + shine (match Steam: target ".mg-tilt")
  const handleCardMove = (e) => {
    const card = e.currentTarget.querySelector(".mg-tilt");
    if (!card) return;
    const rect = layoutRect(card);
    const x = layoutCoordinate(e.clientX) - rect.left;
    const y = layoutCoordinate(e.clientY) - rect.top;
    const px = (x / rect.width) * 2 - 1;
    const py = (y / rect.height) * 2 - 1;
    const rotateY = px * 8;
    const rotateX = -py * 8;
    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
    card.style.setProperty("--sx", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--sy", `${(y / rect.height) * 100}%`);
  };
  const handleCardLeave = (e) => {
    const card = e.currentTarget.querySelector(".mg-tilt");
    if (!card) return;
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
    card.style.setProperty("--sx", "50%");
    card.style.setProperty("--sy", "50%");
  };

  const metadataMap = useMemo(() => {
    const map = {};
    metadata.forEach((game) => {
      if (game?.app_id) map[String(game.app_id).trim()] = game;
    });
    return map;
  }, [metadata]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    metadata.forEach((game) => {
      if (game.tags) game.tags.split(",").forEach((t) => tagSet.add(t.trim()));
    });
    return [...tagSet].sort();
  }, [metadata]);

  const allPublishers = useMemo(() => {
    const publisherSet = new Set();
    metadata.forEach((game) => {
      if (game.publishers) {
        game.publishers.split(/[,;]/).forEach((p) => {
          const clean = p.trim();
          if (clean) publisherSet.add(clean);
        });
      }
    });
    return [...publisherSet].sort();
  }, [metadata]);

  useEffect(() => {
    if (!metadataMap || Object.keys(metadataMap).length === 0) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [latestRows, growthRows] = await Promise.all([
          loadCsvGz(LATEST_FOLLOWERS_URL),
          loadCsvGz(FOLLOWERS_GROWTH_URL),
        ]);

        // Latest snapshot (followers + dt)
        const latestMap = new Map();
        for (const r of latestRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          latestMap.set(appId, {
            followers: Number(r.followers) || 0,
            dt: r.dt || null,
          });
        }

        // Growth rows (nullable growths)
        const toNumOrNull = (v) =>
          v === null || v === undefined || v === "" ? null : Number(v);

        const growthMap = new Map();
        for (const r of growthRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          growthMap.set(appId, {
            current_followers: Number(r.current_followers) || 0,
            growth_7d: toNumOrNull(r["7_day_growth"]),
            growth_14d: toNumOrNull(r["14_day_growth"]),
            growth_21d: toNumOrNull(r["21_day_growth"]),
          });
        }

        const allAppIds = new Set([
          ...Array.from(latestMap.keys()),
          ...Array.from(growthMap.keys()),
        ]);

        const excludedTypes = new Set([
          "episode",
          "hardware",
          "music",
          "series",
          "video",
          "unlisted",
          "not found",
        ]);

        const todayISO = new Date().toISOString().split("T")[0];
        const unified = [];
        for (const appId of allAppIds) {
          if (!(appId > 300000)) continue;
          const meta = metadataMap[String(appId)];
          if (!meta) continue;

          const contentType = (meta.type || "").toLowerCase();
          if (excludedTypes.has(contentType)) continue;

          const latest = latestMap.get(appId);
          const growth = growthMap.get(appId);

          // Prefer the latest snapshot; fall back to growth.current_followers
          const followers =
            latest?.followers ?? null ?? growth?.current_followers ?? 0;
          if (!followers || followers <= 0) continue;

          const releaseDateStr = (meta.release_date || "").trim();
          const releaseISO = releaseDateStr ? releaseDateStr.split("T")[0] : "";
          const futureOrUnknown = !releaseDateStr || releaseISO > todayISO;
          if (!futureOrUnknown) continue;

          // ---- 7d growth fallback logic ----
          // If 7-day growth is missing (null), treat it as "all growth so far"
          // i.e., use current_followers (or followers).
          const growth7Raw = growth?.growth_7d;
          const computed7 =
            growth7Raw == null
              ? growth?.current_followers ?? followers ?? 0
              : growth7Raw;

          unified.push({
            ...meta,
            app_id: String(appId),
            followers,
            growth_7d: computed7,
            growth_14d: growth?.growth_14d ?? null,
            growth_21d: growth?.growth_21d ?? null,
            // map 30d -> 21d for now (leave null if missing)
            growth_30d: growth?.growth_21d ?? null,
            tags: meta?.tags?.split(",").map((t) => t.trim()) || [],
            video: (meta.video || meta.trailer || meta.video_url || "").trim(),
          });
        }

        unified.sort((a, b) => (b.followers || 0) - (a.followers || 0));
        setGames(unified);
        setDisplayedGames(unified);
      } catch (err) {
        console.error("❌ Failed to load followers CSV.GZ:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [metadataMap]);

  const handleRemove = (app_id) => setHiddenGames((prev) => [...prev, app_id]);

  const handleReset = () => {
    setHiddenGames([]);
    setSelectedTag(null);
    setSelectedPublisher(null);
    setDisplayedGames(games);
  };

  const handleExportCSV = () => {
    const visibleGames = displayedGames.filter(
      (g) => !hiddenGames.includes(g.app_id)
    );
    const csv = Papa.unparse(visibleGames);
    const utf8Bom = "\uFEFF";
    const blob = new Blob([utf8Bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "followers_games.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReleasedThisWeek = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    const startISO = start.toISOString().split("T")[0];
    const endISO = end.toISOString().split("T")[0];
    const filtered = games.filter((g) => {
      if (!g.release_date) return false;
      const releaseDateISO = new Date(g.release_date)
        .toISOString()
        .split("T")[0];
      return releaseDateISO >= startISO && releaseDateISO <= endISO;
    });
    setDisplayedGames(filtered);
  };

  // ---------- Always call hooks (no early returns) ----------
  const displayedSafe = loading ? [] : displayedGames;

  const filtered = useMemo(() => {
    return displayedSafe
      .filter((g) => !hiddenGames.includes(g.app_id))
      .filter(
        (g) => g.name && g.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter(
        (g) => !selectedTag || (g.tags && g.tags.includes(selectedTag.value))
      )
      .filter((g) => {
        if (!selectedPublisher) return true;
        // robust publisher match (either "publisher" or "publishers" CSV)
        const pubStr = g.publisher || g.publishers || "";
        return pubStr
          .split(/[,;]/)
          .map((s) => s.trim().toLowerCase())
          .includes(selectedPublisher.value.trim().toLowerCase());
      })
      .filter((g) => g.followers > 0)
      .filter((g) => {
        if (includeLowFollowerGames) return true;
        return g.followers >= 100;
      })
      .filter((g) => {
        if (!showUnreleasedOnly) return true;
        if (!g.release_date) return false;
        return new Date(g.release_date) > new Date();
      });
  }, [
    displayedSafe,
    hiddenGames,
    searchTerm,
    selectedTag,
    selectedPublisher,
    includeLowFollowerGames,
    showUnreleasedOnly,
  ]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (sortType === "growth_7d")
      copy.sort(
        (a, b) => (b.growth_7d ?? -Infinity) - (a.growth_7d ?? -Infinity)
      );
    else if (sortType === "growth_30d")
      copy.sort(
        (a, b) => (b.growth_30d ?? -Infinity) - (a.growth_30d ?? -Infinity)
      );
    else copy.sort((a, b) => (b.followers || 0) - (a.followers || 0));
    return copy;
  }, [filtered, sortType]);

  const totalFollowers = useMemo(
    () => sorted.reduce((sum, g) => sum + (g.followers || 0), 0),
    [sorted]
  );

  // ---------- Lazy/windowed rendering ----------
  const INITIAL_BATCH = 120;
  const BATCH_SIZE = 80;
  const [itemsShown, setItemsShown] = useState(INITIAL_BATCH);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setItemsShown(INITIAL_BATCH);
  }, [
    searchTerm,
    selectedTag,
    selectedPublisher,
    showUnreleasedOnly,
    includeLowFollowerGames,
    sortType,
    displayedGames,
    hiddenGames,
    loading,
  ]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setItemsShown((prev) =>
            prev >= sorted.length
              ? prev
              : Math.min(sorted.length, prev + BATCH_SIZE)
          );
        }
      },
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sorted.length]);

  const visible = useMemo(
    () => sorted.slice(0, itemsShown),
    [sorted, itemsShown]
  );
  const hasMore = itemsShown < sorted.length;

  // Same embed helper as Steam
  const getVideoEmbed = (raw) => {
    if (!raw) return null;
    const url = raw.trim();
    const lower = url.toLowerCase();
    if (/\.(mp4|webm|ogg)(\?|#|$)/.test(lower)) {
      return { type: "file", src: url };
    }
    const yt = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
    );
    if (yt) {
      const id = yt[1];
      return {
        type: "youtube",
        src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&showinfo=0&loop=1&playlist=${id}`,
      };
    }
    return null;
  };

  return (
    <div
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      {/* Sidebar (match Steam: fixed width, no hide button, no "Filters" label) */}
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "calc(var(--mg-vh) * 100)",
          overflowY: "auto",
          padding: 20,
          background: "#111",
          borderRight: "1px solid #333",
          width: 260,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* Dashboard name in the sidebar */}
        <h1
          style={{
            fontSize: 28,
            fontWeight: "bold",
            color: "#fff",
            margin: "4px 0 6px 0",
            lineHeight: "28px",
          }}
        >
          Steam
        </h1>

        <TextInputField
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div style={{ position: "relative", zIndex: 20 }}>
          <VirtualizedDropdown
            placeholder="All Tags"
            value={selectedTag}
            onChange={setSelectedTag}
            options={allTags.map((t) => ({ label: t, value: t }))}
            isClearable
          />
        </div>

        <div style={{ position: "relative", zIndex: 20 }}>
          <VirtualizedDropdown
            placeholder="All Publishers"
            value={selectedPublisher}
            onChange={setSelectedPublisher}
            options={allPublishers.map((p) => ({ label: p, value: p }))}
            isClearable
          />
        </div>

        <button
          onClick={handleReleasedThisWeek}
          style={{
            background: "#3fa9f5",
            border: "none",
            borderRadius: 8,
            padding: "8px 12px",
            color: "#000",
            fontWeight: "bold",
          }}
        >
          Released This Week
        </button>
        <button
          onClick={handleReset}
          style={{
            background: "#333",
            border: "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: "#fff",
          }}
        >
          Reset Games
        </button>
        <button
          onClick={() => setShowUnreleasedOnly(!showUnreleasedOnly)}
          style={{
            background: showUnreleasedOnly ? "#3fa9f5" : "#333",
            border: showUnreleasedOnly ? "none" : "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: showUnreleasedOnly ? "#000" : "#fff",
            fontWeight: showUnreleasedOnly ? "bold" : "normal",
          }}
        >
          {showUnreleasedOnly ? "✔ Showing Unreleased" : "Show Unreleased Only"}
        </button>
        <button
          onClick={() => setIncludeLowFollowerGames(!includeLowFollowerGames)}
          style={{
            background: includeLowFollowerGames ? "#3fa9f5" : "#333",
            border: includeLowFollowerGames ? "none" : "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: includeLowFollowerGames ? "#000" : "#fff",
            fontWeight: includeLowFollowerGames ? "bold" : "normal",
          }}
        >
          {includeLowFollowerGames
            ? "✔ Showing <100 Follower Games"
            : "Show Games with <100 Followers"}
        </button>
        <button
          onClick={() => setSortType("followers")}
          style={{
            background: sortType === "followers" ? "#3fa9f5" : "#333",
            border: sortType === "followers" ? "none" : "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: sortType === "followers" ? "#000" : "#fff",
            fontWeight: sortType === "followers" ? "bold" : "normal",
            width: "100%",
          }}
        >
          Sort by Total Followers
        </button>

        <button
          onClick={() => setSortType("growth_7d")}
          style={{
            background: sortType === "growth_7d" ? "#3fa9f5" : "#333",
            border: sortType === "growth_7d" ? "none" : "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: sortType === "growth_7d" ? "#000" : "#fff",
            fontWeight: sortType === "growth_7d" ? "bold" : "normal",
            width: "100%",
          }}
        >
          Sort by 7-Day Growth
        </button>

        <button
          onClick={() => setSortType("growth_30d")}
          style={{
            background: sortType === "growth_30d" ? "#3fa9f5" : "#333",
            border: sortType === "growth_30d" ? "none" : "1px solid #555",
            borderRadius: 8,
            padding: "8px 12px",
            color: sortType === "growth_30d" ? "#000" : "#fff",
            fontWeight: sortType === "growth_30d" ? "bold" : "normal",
            width: "100%",
          }}
        >
          Sort by 30-Day Growth
        </button>
        <CsvExportButton
          onClick={handleExportCSV}
          style={{
            background: "#3fa9f5",
            border: "none",
            borderRadius: 8,
            padding: "8px 12px",
            color: "#000",
            fontWeight: "bold",
          }}
        >
          Export to CSV
        </CsvExportButton>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: 20 }}>
        {/* Loading overlay */}
        {loading && (
          <div
            style={{
              height: "calc(var(--mg-vh) * 60)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/redesign/logos/icon/mindGAME Tree Logo.png`}
              alt="Loading..."
              className="pulse-logo"
            />
            <style>{`
              .pulse-logo { width: 100px; height: 100px; animation: pulseGlow 1.8s ease-in-out infinite; }
              @keyframes pulseGlow {
                0% { transform: scale(1); filter: drop-shadow(0 0 2px #111); }
                50% { transform: scale(1.05); filter: drop-shadow(0 0 10px #1a1a1a); }
                100% { transform: scale(1); filter: drop-shadow(0 0 2px #111); }
              }
            `}</style>
          </div>
        )}

        {!loading && displayedGames.length > 0 && (
          <>
            <div
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#fff",
                marginBottom: 10,
              }}
            >
              {filtered.length.toLocaleString()} games ·{" "}
              {totalFollowers.toLocaleString()} total followers
            </div>

            {/* Match Steam styles & popout with autoplay video */}
            <style>{`
              .mg-grid { 
                display: grid; 
                grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); 
                gap: 24px; 
                overflow: visible;
                position: relative;
              }
              .mg-card { position: relative; perspective: 900px; transform-style: preserve-3d; z-index: 0; }
              .mg-card:hover { z-index: 1000; }
              .mg-base {
                position: absolute; inset: 0; transform: translateZ(-22px);
                border-radius: 14px;
                background: linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02));
                box-shadow: 0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12);
                filter: saturate(110%);
                z-index: 0;
                pointer-events: none;
              }
              .mg-tilt {
                position: relative; border-radius: 14px;
                transform: rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
                transition: transform 140ms ease, box-shadow 140ms ease;
                will-change: transform, box-shadow;
                box-shadow: 0 10px 28px rgba(0,0,0,0.55);
                overflow: hidden;
                background: #000;
                z-index: 1;
              }
              .mg-card:hover .mg-tilt { box-shadow: 0 18px 44px rgba(0,0,0,0.65); }
              .mg-img { display: block; width: 100%; height: auto; aspect-ratio: 230 / 107; object-fit: cover; }
              .mg-rank {
                position: absolute; top: 8px; left: 8px; padding: 2px 6px; 
                font-size: 13px; font-weight: 700;
                color: #fff; background: rgba(0,0,0,0.55); border-radius: 4px; z-index: 2; backdrop-filter: blur(2px);
              }
              .mg-footer {
                position: absolute; left: 0; right: 0; bottom: 0; z-index: 2;
                padding: 12px 14px 14px 14px; display: flex; gap: 12px; align-items: baseline; justify-content: space-between;
                background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.78) 100%);
              }
              .mg-title {
                font-weight: 800; font-size: 15px; color: #fff; max-width: 72%;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
              }
              .mg-info-title {
                position: relative; margin: 0 0 11px; padding: 0 0 9px 13px;
                color: #fff; font-size: 15px; font-weight: 900; line-height: 1.2;
                letter-spacing: 0.08em; text-transform: uppercase;
                text-shadow: 0 1px 10px rgba(63,169,245,0.22);
              }
              .mg-info-title::before {
                content: ""; position: absolute; left: 0; top: 1px; bottom: 10px;
                width: 3px; border-radius: 3px;
                background: linear-gradient(180deg, #75d5ff, #3fa9f5 55%, #2672d8);
                box-shadow: 0 0 12px rgba(63,169,245,0.65);
              }
              .mg-info-title::after {
                content: ""; position: absolute; left: 13px; right: 0; bottom: 0;
                height: 1px; background: linear-gradient(90deg, rgba(63,169,245,0.58), rgba(63,169,245,0));
              }
              .mg-stats { font-size: 13px; color: #cfd8e3; white-space: nowrap; font-weight: 600; }
              .mg-shine {
                position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity 150ms ease;
                background:
                  radial-gradient(600px at var(--sx, 50%) var(--sy, 50%), rgba(255,255,255,0.18), transparent 40%),
                  linear-gradient(120deg, rgba(255,255,255,0.12), rgba(63,169,245,0.12) 20%, transparent 60%);
                mix-blend-mode: screen; z-index: 1;
              }
              .mg-card:hover .mg-shine { opacity: 1; }
              .mg-card:hover { transform: translateY(-1px); }

              .mg-link-overlay { position: absolute; inset: 0; z-index: 2; display: block; }

              .mg-remove {
                position: absolute; top: 8px; right: 8px; z-index: 3;
                background: rgba(255,255,255,0.22); border: none; border-radius: 50%; width: 24px; height: 24px;
                color: #fff; font-weight: bold; cursor: pointer;
              }

              .mg-info {
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                background: rgba(34,34,34,0.96);
                border-radius: 12px;
                padding: 10px 12px;
                margin-top: 6px;
                opacity: 0;
                pointer-events: none;
                transition: opacity 160ms ease;
                z-index: 2000;
                font-size: 14px;
                line-height: 1.4;
                border: 1px solid #333;
              }
              .mg-card:hover .mg-info { opacity: 1; }

              .mg-video {
                width: 100%;
                aspect-ratio: 16 / 9;
                background: #000;
                border-radius: 10px;
                border: 1px solid #333;
                overflow: hidden;
                margin-bottom: 10px;
              }
              .mg-video video,
              .mg-video iframe {
                width: 100%;
                height: 100%;
                display: block;
                pointer-events: none;
              }

              .mg-sentinel { width: 100%; height: 1px; }
              .mg-card-wrap { position: relative; }
            `}</style>

            <div className="mg-grid">
              {visible.map((g, i) => {
                const growth7 = g.growth_7d ?? 0;
                const shareText = `${g.followers?.toLocaleString()} · ${
                  growth7 >= 0 ? "+" : ""
                }${Math.abs(growth7).toLocaleString()} (7d)`;

                return (
                  <div
                    key={g.app_id}
                    className="mg-card mg-card-wrap"
                    onMouseMove={handleCardMove}
                    onMouseEnter={() => setHoveredId(g.app_id)}
                    onMouseLeave={(e) => {
                      handleCardLeave(e);
                      setHoveredId(null);
                    }}
                  >
                    <div className="mg-base" />

                    {/* Tilted visuals */}
                    <div className="mg-tilt">
                      <img className="mg-img" src={g.box_art} alt={g.name} />
                      <div className="mg-rank">#{i + 1}</div>
                      <div className="mg-footer">
                        <div className="mg-stats">{shareText}</div>
                      </div>
                      <div className="mg-shine" />
                    </div>

                    {/* Full overlay link for navigation */}
                    <Link
                      to={`/game/${g.app_id}`}
                      className="mg-link-overlay"
                      aria-label={`Open ${g.name}`}
                    />

                    {/* Remove (stays clickable and cancels navigation) */}
                    <button
                      className="mg-remove"
                      type="button"
                      aria-label="Remove game"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(g.app_id);
                      }}
                    >
                      ×
                    </button>

                    {/* Metadata hover pop-out with autoplay video */}
                    <div className="mg-info">
                      {hoveredId === g.app_id &&
                        g.video &&
                        (() => {
                          const embed = getVideoEmbed(g.video);
                          if (!embed) return null;
                          return (
                            <div className="mg-video">
                              <SteamVideoPreview tags={g.tags} title={g.name}>
                                {embed.type === "file" ? (
                                  <video
                                    src={embed.src}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                  />
                                ) : (
                                  <iframe
                                    src={embed.src}
                                    title={`${g.name} trailer`}
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                  />
                                )}
                              </SteamVideoPreview>
                            </div>
                          );
                        })()}

                      <div className="mg-info-title">{g.name}</div>
                      {g.short_description && (
                        <div style={{ marginBottom: 10 }}>
                          {g.short_description}
                        </div>
                      )}

                      {(g.release_date || g.full_price) && (
                        <div style={{ marginBottom: 6 }}>
                          {g.release_date && (
                            <span>
                              <strong>Release:</strong>{" "}
                              {new Date(g.release_date).toLocaleDateString()}
                            </span>
                          )}
                          {g.release_date && g.full_price && <span> / </span>}
                          {g.full_price && (
                            <span>
                              <strong>Price: $</strong> {g.full_price}
                            </span>
                          )}
                        </div>
                      )}

                      {g.publishers && (
                        <div style={{ marginBottom: 6 }}>
                          <strong>Publishers:</strong> {g.publishers}
                        </div>
                      )}

                      {g.developers && (
                        <div style={{ marginBottom: 10 }}>
                          <strong>Developer(s):</strong> {g.developers}
                        </div>
                      )}

                      {Array.isArray(g.tags) && g.tags.length > 0 && (
                        <div>
                          <strong>Tags:</strong> {g.tags.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && <div ref={sentinelRef} className="mg-sentinel" />}
          </>
        )}
      </div>
    </div>
  );
}

export default Followers;
